import { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X, Clock } from 'lucide-react';

interface DatePickerProps {
    value?: string;
    onChange: (date: string) => void;
    placeholder?: string;
    minDate?: string;
    maxDate?: string;
    presets?: boolean;
}

const DAYS = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const MONTHS = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

function addDays(date: Date, days: number): string {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return toDateStr(d);
}
function toDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function DatePicker({ value, onChange, placeholder = 'Pilih tanggal', minDate, maxDate, presets = false }: DatePickerProps) {
    const [open, setOpen] = useState(false);
    const [flipUp, setFlipUp] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const today = new Date();
    const todayStr = toDateStr(today);
    const selected = value ? new Date(value + 'T00:00:00') : null;
    const [viewYear, setViewYear] = useState(selected?.getFullYear() || today.getFullYear());
    const [viewMonth, setViewMonth] = useState(selected?.getMonth() ?? today.getMonth());

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setFlipUp(window.innerHeight - rect.bottom < 380);
        }
    }, [open]);

    useEffect(() => {
        if (selected) {
            setViewYear(selected.getFullYear());
            setViewMonth(selected.getMonth());
        }
    }, [value]);

    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

    const goMonth = (delta: number) => {
        let m = viewMonth + delta;
        let y = viewYear;
        if (m < 0) { m = 11; y--; }
        if (m > 11) { m = 0; y++; }
        setViewMonth(m); setViewYear(y);
    };

    const canGoPrev = useMemo(() => {
        if (!minDate) return true;
        const min = new Date(minDate + 'T00:00:00');
        const prevM = viewMonth === 0 ? 11 : viewMonth - 1;
        const prevY = viewMonth === 0 ? viewYear - 1 : viewYear;
        return new Date(prevY, prevM + 1, 0) >= min;
    }, [minDate, viewMonth, viewYear]);

    const isDisabled = (s: string) => (minDate && s < minDate) || (maxDate && s > maxDate);

    const select = (day: number) => {
        const d = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        if (isDisabled(d)) return;
        onChange(d);
        setOpen(false);
    };

    const formatDisplay = () => {
        if (!selected) return placeholder;
        const dayName = DAYS[selected.getDay()];
        return `${dayName}, ${selected.getDate()} ${MONTHS[selected.getMonth()].slice(0, 3)} ${selected.getFullYear()}`;
    };

    const cells: { day: number; current: boolean; dateStr: string }[] = [];
    for (let i = 0; i < firstDay; i++) {
        const d = prevMonthDays - firstDay + 1 + i;
        const m = viewMonth === 0 ? 12 : viewMonth;
        const y = viewMonth === 0 ? viewYear - 1 : viewYear;
        cells.push({ day: d, current: false, dateStr: `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        cells.push({ day: i, current: true, dateStr: `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}` });
    }
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
        const m = viewMonth + 2 > 12 ? 1 : viewMonth + 2;
        const y = viewMonth + 2 > 12 ? viewYear + 1 : viewYear;
        cells.push({ day: i, current: false, dateStr: `${y}-${String(m).padStart(2, '0')}-${String(i).padStart(2, '0')}` });
    }

    const presetOpts = [
        { label: 'Hari ini', value: addDays(today, 0) },
        { label: 'Besok', value: addDays(today, 1) },
        { label: '+7', value: addDays(today, 7) },
        { label: '+30', value: addDays(today, 30) },
    ].filter(p => !isDisabled(p.value));

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 w-full h-10 rounded-xl px-3 bg-white text-sm font-medium hover:border-brand transition"
                style={{ border: `1px solid ${value ? '#3d8b37' : '#d1d5db'}`, color: value ? '#1f2937' : '#9ca3af' }}
            >
                <Calendar className={`h-4 w-4 ${value ? 'text-brand' : ''}`} />
                <span className="flex-1 text-left truncate">{formatDisplay()}</span>
                {value && (
                    <X
                        className="h-4 w-4 text-gray-400 hover:text-gray-600"
                        onClick={(e) => { e.stopPropagation(); onChange(''); }}
                    />
                )}
            </button>
            {open && (
                <div
                    className={`absolute z-[100] left-0 bg-white rounded-2xl shadow-xl border border-gray-200 p-4 ${flipUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'}`}
                    style={{ width: presets ? 340 : 300 }}
                >
                    <div className="flex items-center justify-between mb-3">
                        <button type="button" onClick={() => canGoPrev && goMonth(-1)} disabled={!canGoPrev} className={`h-8 w-8 flex items-center justify-center rounded-full ${canGoPrev ? 'hover:bg-gray-100' : 'opacity-30'}`}>
                            <ChevronLeft className="h-4 w-4 text-gray-600" />
                        </button>
                        <div className="text-sm font-bold text-gray-800">
                            {MONTHS[viewMonth]} <span className="text-gray-400 font-normal">{viewYear}</span>
                        </div>
                        <button type="button" onClick={() => goMonth(1)} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-gray-100">
                            <ChevronRight className="h-4 w-4 text-gray-600" />
                        </button>
                    </div>
                    <div className="grid grid-cols-7 gap-0 mb-1">
                        {DAYS.map(d => <div key={d} className="text-[10px] font-semibold text-gray-400 text-center py-1.5 uppercase">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-0">
                        {cells.map((cell, i) => {
                            const disabled = !cell.current || isDisabled(cell.dateStr);
                            const sel = cell.current && selected && cell.day === selected.getDate() && viewMonth === selected.getMonth() && viewYear === selected.getFullYear();
                            const tdy = cell.current && cell.day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
                            return (
                                <button key={i} type="button" disabled={disabled === true} onClick={() => cell.current && !isDisabled(cell.dateStr) && select(cell.day)}
                                    className={`h-9 w-9 mx-auto text-xs rounded-full flex items-center justify-center transition
                                        ${!cell.current ? 'text-gray-200' : ''}
                                        ${cell.current && disabled ? 'text-gray-300 line-through opacity-40' : ''}
                                        ${cell.current && !disabled && !sel ? 'hover:bg-brand/10 hover:text-brand-700' : ''}
                                        ${tdy && !sel ? 'font-bold text-brand-700 ring-2 ring-brand/30 ring-inset' : ''}
                                        ${sel ? 'bg-brand text-white font-bold scale-110 shadow-md' : ''}`}>
                                    {cell.day}
                                </button>
                            );
                        })}
                    </div>
                    {presets && presetOpts.length > 0 && (
                        <div className="mt-3 pt-3 flex flex-wrap gap-1.5 border-t border-gray-100">
                            {presetOpts.map(p => (
                                <button key={p.label} type="button"
                                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full ${value === p.value ? 'bg-brand text-white' : 'bg-gray-50 text-gray-600 hover:bg-brand/10 hover:text-brand-700'}`}
                                    onClick={() => { onChange(p.value); setOpen(false); }}>
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {!presets && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                            <button type="button" className="w-full flex items-center justify-center gap-1.5 text-xs text-brand-700 font-bold py-1.5 rounded-lg hover:bg-brand/10"
                                onClick={() => { if (!isDisabled(todayStr)) { onChange(todayStr); setOpen(false); } }}
                                disabled={!!isDisabled(todayStr)}>
                                <Clock className="h-3 w-3" /> Hari ini
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
